import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  User, Shield, Bell, Crown, Lock, HelpCircle, LogOut, ChevronRight, Mail, Phone, Coins, Sparkles, Landmark,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDate, symbolFor, CURRENCIES } from "@/lib/format";

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { data: sub } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data,
  });

  const isPlus = user?.subscription_status === "plus";
  const currencyLabel = CURRENCIES.find((c) => c.code === (user?.default_currency || "NGN"))?.label || "Nigerian Naira";

  // Sync the session if the server just reconciled a paid subscription.
  React.useEffect(() => {
    if (sub?.plan === "plus" && user && user.subscription_status !== "plus") {
      refreshUser();
    }
  }, [sub, user, refreshUser]);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/welcome", { replace: true });
  };

  const groups = [
    { icon: User, label: "Account", hint: user?.name, to: "/settings/account" },
    { icon: Landmark, label: "Repayment account", hint: "Where debts are repaid", to: "/settings/repayment" },
    { icon: Shield, label: "Security", hint: "Password", to: "/settings/security" },
    { icon: Bell, label: "Notifications", hint: "Reminders", to: "/settings/notifications" },
    { icon: Lock, label: "Privacy", hint: "Your data is private", to: "/settings/privacy" },
    { icon: HelpCircle, label: "Help", hint: "", to: "/settings/help" },
  ];

  return (
    <div className="pb-4">
      <h1 className="font-display text-2xl font-bold text-foreground">Profile</h1>

      {/* Identity card */}
      <div className="mt-4 rounded-3xl bg-card border border-border p-5 flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground font-display text-xl font-bold flex items-center justify-center">
          {(user?.name || "?")[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-display text-lg font-bold text-foreground truncate">{user?.name}</p>
          <p className="text-sm text-muted-foreground truncate flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{user?.email}</p>
          {user?.phone && <p className="text-sm text-muted-foreground truncate flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{user.phone}</p>}
        </div>
      </div>

      {/* Plan card */}
      <button
        data-testid="profile-plan-card"
        onClick={() => navigate("/upgrade")}
        className={`mt-3 w-full text-left rounded-3xl p-5 flex items-center justify-between ${isPlus ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
      >
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isPlus ? "bg-white/20" : "bg-white"}`}>
            <Crown className={`h-5 w-5 ${isPlus ? "text-amber-300" : "text-primary"}`} />
          </div>
          <div>
            <p className={`font-display font-bold ${isPlus ? "text-white" : "text-foreground"}`}>{isPlus ? "Debii Plus" : "Free plan"}</p>
            <p className={`text-xs ${isPlus ? "text-white/80" : "text-muted-foreground"}`}>
              {isPlus ? `Renews ${fmtDate(user?.subscription_expires_at)}` : "Upgrade for unlimited debts"}
            </p>
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 ${isPlus ? "text-white/80" : "text-muted-foreground"}`} />
      </button>

      {/* Currency */}
      <button
        data-testid="profile-currency"
        onClick={() => navigate("/settings/account")}
        className="mt-3 w-full text-left rounded-2xl bg-card border border-border px-4 py-3.5 flex items-center justify-between hover:bg-secondary/50"
      >
        <span className="text-sm text-foreground flex items-center gap-2"><Coins className="h-4 w-4 text-muted-foreground" /> Default currency</span>
        <span className="text-sm font-semibold text-foreground">{symbolFor(user?.default_currency)} {currencyLabel} ({user?.default_currency || "NGN"})</span>
      </button>

      {/* Settings groups */}
      <div className="mt-3 rounded-2xl bg-card border border-border divide-y divide-border overflow-hidden">
        {groups.map((g) => (
          <button
            key={g.label}
            data-testid={`settings-link-${g.label.toLowerCase()}`}
            onClick={() => navigate(g.to)}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-secondary/50"
          >
            <span className="text-sm text-foreground flex items-center gap-2"><g.icon className="h-4 w-4 text-muted-foreground" /> {g.label}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">{g.hint} <ChevronRight className="h-4 w-4" /></span>
          </button>
        ))}
      </div>

      <button
        data-testid="profile-logout"
        onClick={doLogout}
        className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5 flex items-center justify-center gap-2 text-red-600 font-semibold"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>

      <div className="mt-8 flex flex-col items-center gap-1 opacity-70">
        <Logo size={22} />
        <p className="text-xs text-muted-foreground">Debii records what you enter. It doesn't lend or hold money.</p>
      </div>
    </div>
  );
}
